import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorState from '../../components/ErrorState';
import EmptyState from '../../components/EmptyState';
import * as analytics from '../../api/analytics.api';
import { errorMessage } from '../../api/client';
import { formatMoney, formatNumber } from '../../utils/format';

function Section({ title, children }) {
  return (
    <div className="card" style={{ marginTop: 'var(--s-4)' }}>
      <div className="card-title"><h3>{title}</h3></div>
      {children}
    </div>
  );
}

function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      analytics.revenue({ groupBy: 'day' }),
      analytics.packagesReport(),
      analytics.vendorsReport(),
      analytics.usersReport()
    ])
      .then(([rev, pkgs, vens, users]) => { if (!cancelled) setData({ rev, pkgs, vens, users }); })
      .catch((e) => { if (!cancelled) setErr(errorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingSpinner size="lg" />;
  if (err) return <ErrorState message={err} />;

  const { rev, pkgs, vens, users } = data;
  const maxRev = Math.max(1, ...(rev?.series || []).map((p) => p.revenue || 0));

  return (
    <>
      <PageHeader title="Analytics" subtitle="Revenue, packages, vendors and users." />

      <Section title="Revenue (daily)">
        {rev?.series?.length ? (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 220 }}>
            {rev.series.slice(-30).map((p) => (
              <div key={p.period} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <div
                  title={`${p.period} · ${formatMoney(p.revenue)}`}
                  style={{
                    width: '100%',
                    height: `${Math.max(2, Math.round((p.revenue / maxRev) * 190))}px`,
                    borderRadius: 6,
                    background: 'linear-gradient(180deg, var(--brand-400), var(--brand-700))'
                  }}
                />
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{p.period.slice(5)}</div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState title="No revenue yet" />
        )}
        <div className="row between" style={{ marginTop: 'var(--s-4)' }}>
          <div className="muted text-sm">Total: <strong style={{ color: 'var(--text)' }}>{formatMoney(rev?.totals?.revenue || 0)}</strong> across {formatNumber(rev?.totals?.orders || 0)} orders</div>
        </div>
      </Section>

      <Section title="Top packages">
        {pkgs?.items?.length ? (
          <table className="data-table">
            <thead><tr><th>Package</th><th>Orders</th><th>Revenue</th></tr></thead>
            <tbody>
              {pkgs.items.map((p) => (
                <tr key={p.packageId}>
                  <td className="cell-strong">{p.name || '—'}</td>
                  <td>{formatNumber(p.orders)}</td>
                  <td>{formatMoney(p.revenue, p.currency || 'MYR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <EmptyState title="No data" />}
      </Section>

      <Section title="Vendor performance">
        {vens?.items?.length ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Assigned</th>
                <th>Completed</th>
                <th>Rate</th>
                <th>Avg turnaround</th>
                <th>Reports</th>
              </tr>
            </thead>
            <tbody>
              {vens.items.map((v) => (
                <tr key={v.vendorId}>
                  <td>
                    <div className="cell-strong">{v.name || '—'}</div>
                    <div className="muted text-xs">{v.email}</div>
                  </td>
                  <td>{v.assigned}</td>
                  <td>{v.completed}</td>
                  <td>{Math.round((v.completionRate || 0) * 100)}%</td>
                  <td>{v.avgTurnaroundHours != null ? `${v.avgTurnaroundHours}h` : '—'}</td>
                  <td>{v.reportsUploaded || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <EmptyState title="No vendor data yet" />}
      </Section>

      <Section title="Top users by spend">
        {users?.activity?.length ? (
          <table className="data-table">
            <thead><tr><th>User</th><th>Orders</th><th>Paid</th><th>Total spent</th></tr></thead>
            <tbody>
              {users.activity.slice(0, 20).map((u) => (
                <tr key={u.userId}>
                  <td>
                    <div className="cell-strong">{u.name || '—'}</div>
                    <div className="muted text-xs">{u.email}</div>
                  </td>
                  <td>{u.orders}</td>
                  <td>{u.paid}</td>
                  <td>{formatMoney(u.totalSpent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <EmptyState title="No user activity yet" />}
      </Section>
    </>
  );
}

export default Analytics;
